import { Button, Modal, Text, useMediaQuery } from '@querodelivery/uni-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { useCities } from '@/_hooks/use-cities';
import { useAppSelector } from '@/_store/hooks';
import { selectUser } from '@/_store/modules/auth/selectors';
import type { CreateMissionFormType } from '../../types';
import { buildMissionConfirmationData } from '../../utils/build-mission-confirmation-data';
import { DetailLine } from '../detail-line/detail-line';
import { ExpandableAudienceLine } from '../expandable-audience-line/expandable-audience-line';
import { MissionMobilePreview } from '../mission-mobile-preview/mission-mobile-preview';
import { ConfirmMissionModalSkeleton } from './confirm-mission-modal-skeleton';
import styles from './confirm-mission-modal.module.scss';

type ConfirmMissionModalProps = {
  isOpen: boolean;
  formValues?: CreateMissionFormType | null;
  isLoading?: boolean;
  mode?: 'confirm' | 'view';
  isEdit?: boolean;
  creatorName?: string;
  totalDeliverymen?: number;
  onClose: () => void;
  onCancel?: () => void;
  onCancelMission?: () => void;
  onConfirm?: () => void;
  onEdit?: () => void;
};

export const ConfirmMissionModal = ({
  isOpen,
  formValues,
  isLoading = false,
  mode = 'confirm',
  isEdit = false,
  creatorName,
  totalDeliverymen,
  onClose,
  onCancel,
  onCancelMission,
  onConfirm,
  onEdit,
}: ConfirmMissionModalProps) => {
  const user = useAppSelector(selectUser);
  const { CITIES_OPTIONS } = useCities();
  const isMobile = useMediaQuery('lg-down');
  const showPreview = !isMobile;
  const isViewMode = mode === 'view';
  const isCloseBlocked = !isViewMode && isLoading;

  const title = isViewMode
    ? 'Missão agendada:'
    : isEdit
    ? 'Atualizar missão:'
    : 'Agendar missão:';

  const subtitle = isViewMode
    ? 'Confirme as informações da missão:'
    : isEdit
    ? 'Confirme as alterações da missão:'
    : 'Confirme as informações da missão:';

  const confirmation = formValues
    ? buildMissionConfirmationData({
        formValues,
        creatorName: creatorName ?? user?.nome ?? '',
        cityOptions: CITIES_OPTIONS,
        totalDeliverymen,
      })
    : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnDimmerClick={!isCloseBlocked}
      header={showPreview ? undefined : title}
      showCloseIcon={!showPreview && !isCloseBlocked}
      width={showPreview ? 'auto' : '90%'}
      className={showPreview ? styles.modalShell : undefined}
      contentClassName={showPreview ? styles.modalShellContent : undefined}
    >
      <div className={showPreview ? styles.layout : undefined}>
        {showPreview && formValues ? (
          <MissionMobilePreview formValues={formValues} />
        ) : null}

        <div className={showPreview ? styles.formCard : undefined}>
          {showPreview && (
            <div className={styles.formHeader}>
              <Text
                as="h2"
                variant="headingXl"
                fontWeight="bold"
                tone="highlight"
              >
                {title}
              </Text>
              <button
                type="button"
                className={styles.closeButton}
                onClick={onClose}
                aria-label="Fechar modal"
                disabled={isCloseBlocked}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
          )}

          <div
            className={
              showPreview ? styles.content : styles.contentWithoutPreview
            }
          >
            {!confirmation ? (
              <ConfirmMissionModalSkeleton />
            ) : (
              <>
                <Text variant="bodyMd" tone="secondary" fontWeight="medium">
                  {subtitle}
                </Text>

                <div className={styles.infoCard}>
                  <Text variant="bodyMd" tone="base" fontWeight="medium">
                    Missão: {confirmation.missionName}
                  </Text>
                </div>

                <div className={styles.detailsCard}>
                  <div className={styles.detailRow}>
                    <div className={styles.detailGroup}>
                      <DetailLine
                        label="Data de inicio"
                        value={confirmation.startDateLabel}
                      />
                      <DetailLine
                        label="Data de finalização"
                        value={confirmation.endDateLabel}
                      />
                    </div>
                  </div>

                  <div className={styles.detailRow}>
                    <div className={styles.detailGroup}>
                      <DetailLine
                        label="Hora de inicio"
                        value={confirmation.startTimeLabel}
                      />
                      <DetailLine
                        label="Hora de finalização"
                        value={confirmation.endTimeLabel}
                      />
                    </div>
                  </div>

                  <div className={styles.detailRow}>
                    <div className={styles.detailGroup}>
                      <DetailLine
                        label="Objetivo"
                        value={confirmation.objectiveLabel}
                      />
                      <DetailLine
                        label="Premiação"
                        value={confirmation.rewardLabel}
                      />
                    </div>
                  </div>

                  <ExpandableAudienceLine
                    label={confirmation.audienceLabel}
                    items={confirmation.audienceItems}
                  />
                </div>

                <div className={styles.infoCard}>
                  <Text variant="bodyMd" tone="base" fontWeight="medium">
                    Criador: {confirmation.creatorName}
                  </Text>
                </div>

                {isViewMode ? (
                  onEdit || onCancelMission ? (
                    <div className={styles.actions}>
                      {onEdit ? (
                        <Button
                          size="xl"
                          type="button"
                          variant="primary"
                          onClick={onEdit}
                          disabled={isLoading || !formValues}
                        >
                          editar missão
                        </Button>
                      ) : null}
                      {onCancelMission ? (
                        <Button
                          size="xl"
                          type="button"
                          variant="outline"
                          onClick={onCancelMission}
                          disabled={isLoading || !formValues}
                        >
                          cancelar missão
                        </Button>
                      ) : null}
                    </div>
                  ) : null
                ) : (
                  <div className={styles.actions}>
                    <Button
                      variant="outline"
                      size="xl"
                      type="button"
                      onClick={onCancel}
                      disabled={isLoading}
                    >
                      cancelar
                    </Button>
                    <Button
                      size="xl"
                      type="button"
                      loading={isLoading}
                      disabled={isLoading}
                      onClick={onConfirm}
                    >
                      {isEdit ? 'atualizar missão' : 'agendar missão'}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
